import React, { useState, useEffect } from 'react';
import moment from 'moment';
import getUserIdFromToken from '../../Utils/authUtils';
import { createTask, updateTask, deleteTask } from './apiService';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Select, Button } from 'antd';
import './Calendar.css';

const { TextArea } = Input;
const { Option } = Select;

const TaskPopup = ({ 
  isOpen, 
  task, 
  onClose, 
  onSave, 
  date, 
  tasks = [], 
  classes = [], 
  users = [],
  selectedTeachers = []
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceCount, setRecurrenceCount] = useState(1);
  const [groupId, setGroupId] = useState(uuidv4());
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [eventType, setEventType] = useState('regular'); 
  const [selectedDate, setSelectedDate] = useState('');
  
  // 👇 Separate hour and minute states
  const [startHour, setStartHour] = useState('09');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('10');
  const [endMinute, setEndMinute] = useState('00');

  const isNewTask = !task;

  // Reset form function
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setRecurrence('none');
    setRecurrenceCount(1);
    setGroupId(uuidv4());
    setSelectedUsers([]);
    setSelectedRoles([]);
    setEventType('regular');
    setSelectedDate('');
    setStartHour('09');
    setStartMinute('00');
    setEndHour('10');
    setEndMinute('00');
  };

  // 👇 Handle minute input change with validation
  const handleMinuteChange = (value, setter) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // Limit to 2 digits
    if (numericValue.length <= 2) {
      setter(numericValue);
    }
  };

  // 👇 Format minute on blur (add leading zero if needed, cap at 59)
  const handleMinuteBlur = (value, setter) => {
    if (value === '') {
      setter('00');
    } else {
      let numValue = parseInt(value, 10);
      if (numValue > 59) numValue = 59;
      setter(String(numValue).padStart(2, '0'));
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (task) {
        // Editing existing task
        setTitle(task.title || "");
        setDescription(task.description || "");
        setGroupId(task.groupId || task.groupid || task.extendedProps?.groupid || uuidv4());

        const sharedUsers = task.extendedProps?.sharedUsers || [];
        const sharedRoles = task.extendedProps?.sharedRoles || [];

        setSelectedUsers(sharedUsers);
        setSelectedRoles(sharedRoles);

        const event = task.extendedProps?.event_type || "regular";
        setEventType(event);

        const startMoment = moment(task.start);
        const endMoment = moment(task.end);

        setSelectedDate(startMoment.format("YYYY-MM-DD"));
        setStartHour(startMoment.format("HH"));
        setStartMinute(startMoment.format("mm"));
        setEndHour(endMoment.format("HH"));
        setEndMinute(endMoment.format("mm"));

      } else if (date) {
        // Creating new task - reset form first, then set defaults
        resetForm();
        
        // Pre-populate with selected teachers for new tasks
        setSelectedUsers(selectedTeachers);
        
        const startMoment = moment(date.start);
        const endMoment = moment(date.end);

        setSelectedDate(startMoment.format("YYYY-MM-DD"));
        setStartHour(startMoment.format("HH"));
        setStartMinute(startMoment.format("mm"));
        setEndHour(endMoment.format("HH"));
        setEndMinute(endMoment.format("mm"));
      } else {
        // Creating new task without date
        resetForm();
        setSelectedUsers(selectedTeachers);
      }
    }
  }, [task, date, isOpen, selectedTeachers]);

  // Format date for display (DD/MM/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD MMM YYYY');
  };

  // Enhanced close handler
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSaveTask = async () => {
    const userId = getUserIdFromToken();
    if (!userId) {
      console.error("User ID not found");
      return;
    }

    if (!selectedDate) {
      Modal.error({
        title: t("Invalid Date"),
        content: t("Please select a date."),
      });
      return;
    }

    // 👇 Build time strings from hour and minute
    const startTime = `${startHour}:${startMinute}`;
    const endTime = `${endHour}:${endMinute}`;

    const start = moment(`${selectedDate} ${startTime}`, "YYYY-MM-DD HH:mm");
    const end = moment(`${selectedDate} ${endTime}`, "YYYY-MM-DD HH:mm");

    if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) {
      Modal.error({
        title: t("Invalid Time"),
        content: t("End time must be after start time."),
      });
      return;
    }

    const isOverlapping = classes.some(cls => {
      const classStart = moment(cls.start);
      const classEnd = moment(cls.end);

      return (
        start.isBetween(classStart, classEnd, null, "[)") ||
        end.isBetween(classStart, classEnd, null, "(]") ||
        (start.isBefore(classStart) && end.isAfter(classEnd))
      );
    });

    if (isOverlapping) {
      Modal.error({
        title: t("Time Slot Conflict"),
        content: t("The selected time overlaps with an existing class."),
      });
      return;
    }

    const finalEventType = eventType || "regular";

    const taskDetails = {
      userId,
      title,
      description,
      start: start.format("YYYY-MM-DD HH:mm:ss"),
      end: end.format("YYYY-MM-DD HH:mm:ss"),
      recurrence: isNewTask ? recurrence : task.recurrence,
      recurrenceCount,
      groupId: task
        ? task.groupId || task.groupid || task.extendedProps?.groupid
        : groupId,
      event_type: finalEventType,
      sharedWith: {
        users: selectedUsers,
        roles: selectedRoles,
        all: false,
      },
    };

    try {
      if (task) {
        const taskId =
          task.eventid ||
          task.id.replace(/^event-/, "").replace(/^task-/, "").replace(/^class-/, "");

        await updateTask(taskId, taskDetails);

        Modal.success({
          title: t("Success"),
          content: t("Task updated successfully"),
        });
      } else {
        await createTask(taskDetails);

        Modal.success({
          title: t("Success"),
          content: t("Task created successfully"),
        });
      }

      resetForm();
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      Modal.error({
        title: t("Error"),
        content: t("Failed to save task"),
      });
    }
  };

  const handleDeleteTask = async (deleteGroup) => {
    try {
      const taskId = task.eventid || task.id.replace(/^event-/, '').replace(/^task-/, '').replace(/^class-/, '');
      
      console.log('🗑️ Deleting event with ID:', taskId);
      
      const deleteParams = { deleteGroup: deleteGroup ? 'true' : 'false' };
      await deleteTask(taskId, deleteParams);
      
      Modal.success({
        title: t('Success'),
        content: t('Task deleted successfully'),
      });
      
      resetForm();
      onSave();
      onClose();
    } catch (error) {
      Modal.error({
        title: t('Error'),
        content: t('Failed to delete task'),
      });
      console.error('Error deleting task:', error);
    }
  };

  const openDeleteModal = () => {
    setIsDeleteModalVisible(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalVisible(false);
  };

  return (
    <>
      <Modal
        title={task ? t('Edit Task') : t('New Task')}
        visible={isOpen}
        onCancel={handleClose}
        width={600}
        footer={[
          task && (
            <Button key="delete" danger onClick={openDeleteModal}>
              {t('Delete')}
            </Button>
          ),
          <Button key="cancel" onClick={handleClose}>
            {t('Cancel')}
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveTask}>
            {t('Save')}
          </Button>,
        ]}
      >
        <div className="form-row">
          <label>{t('Title')}<span style={{ color: 'red', marginLeft: 4 }}>*</span></label>
          <Input 
            value={title} 
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('Enter task title')}
            required
          />
        </div>

        <div className="form-row">
          <label>{t('Description')}:</label>
          <TextArea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t('Enter description (optional)')}
          />
        </div>

        <div className="form-row">
          <label>{t('Event Type')}<span style={{ color: 'red', marginLeft: 4 }}>*</span></label>
          <Select 
            value={eventType} 
            onChange={(value) => setEventType(value)}
            style={{ width: '100%' }}
            required
          >
            <Option value="regular">
              <span>✅ {t('Regular')}</span>
            </Option>
            <Option value="class_cancelled">
              <span>❌ {t('Class Cancelled')}</span>
            </Option>
            <Option value="class_reserved">
              <span>📅 {t('Class Reserved')}</span>
            </Option>
          </Select>
        </div>

       <div className="form-row">
  <label>
    {t('Date')}<span style={{ color: 'red', marginLeft: 4 }}>*</span>
    <span style={{ 
      marginLeft: '8px', 
      fontSize: '12px', 
      color: '#8c8c8c', 
      fontWeight: 'normal',
      fontStyle: 'italic'
    }}>
      Format: DD/MM/YYYY
    </span>
  </label>
  
  <div style={{ position: 'relative' }}>
    {/* Hidden native date input */}
    <input
      type="date"
      value={selectedDate}
      onChange={(e) => setSelectedDate(e.target.value)}
      id="hidden-task-date"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        pointerEvents: 'none'
      }}
    />
    
    {/* Visible formatted input */}
    <Input
      readOnly
      value={selectedDate ? formatDateForDisplay(selectedDate) : ''}
      placeholder="DD/MM/YYYY"
      onClick={() => document.getElementById('hidden-task-date')?.showPicker()}
      suffix={<span style={{ cursor: 'pointer' }}>📅</span>}
      style={{
        cursor: 'pointer',
      }}
    />
  </div>
</div>

        {/* 👇 Start Time with separate hour and minute */}
        <div className="form-row">
          <label>{t('Start Time')}<span style={{ color: 'red' }}>*</span>
          <span style={{ 
            marginLeft: '8px', 
            fontSize: '12px', 
            color: '#8c8c8c', 
            fontWeight: 'normal',
            fontStyle: 'italic'
          }}>
            Format: 24hr
          </span></label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Select
              showSearch
              value={startHour}
              onChange={(value) => setStartHour(value)}
              style={{ width: '100px' }}
              placeholder={t('Hour')}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {Array.from({ length: 24 }, (_, i) => {
                const hour = String(i).padStart(2, '0');
                return (
                  <Option key={hour} value={hour}>
                    {hour}
                  </Option>
                );
              })}
            </Select>
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>:</span>
            <Input
              value={startMinute}
              onChange={(e) => handleMinuteChange(e.target.value, setStartMinute)}
              onBlur={(e) => handleMinuteBlur(e.target.value, setStartMinute)}
              placeholder="00"
              maxLength={2}
              style={{ width: '70px', textAlign: 'center' }}
            />
          </div>
        </div>

        {/* 👇 End Time with separate hour and minute */}
        <div className="form-row">
          <label>{t('End Time')}<span style={{ color: 'red' }}>*</span>
          <span style={{ 
            marginLeft: '8px', 
            fontSize: '12px', 
            color: '#8c8c8c', 
            fontWeight: 'normal',
            fontStyle: 'italic'
          }}>
            Format: 24hr
          </span></label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Select
              showSearch
              value={endHour}
              onChange={(value) => setEndHour(value)}
              style={{ width: '100px' }}
              placeholder={t('Hour')}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {Array.from({ length: 24 }, (_, i) => {
                const hour = String(i).padStart(2, '0');
                return (
                  <Option key={hour} value={hour}>
                    {hour}
                  </Option>
                );
              })}
            </Select>
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>:</span>
            <Input
              value={endMinute}
              onChange={(e) => handleMinuteChange(e.target.value, setEndMinute)}
              onBlur={(e) => handleMinuteBlur(e.target.value, setEndMinute)}
              placeholder="00"
              maxLength={2}
              style={{ width: '70px', textAlign: 'center' }}
            />
          </div>
        </div>

        {isNewTask && (
          <>
            <div className="form-row">
              <label>{t('Recurrence')}:</label>
              <Select 
                value={recurrence} 
                onChange={(value) => setRecurrence(value)}
                style={{ width: '100%' }}
              >
                <Option value="none">{t('None')}</Option>
                <Option value="daily">{t('Daily')}</Option>
                <Option value="weekly">{t('Weekly')}</Option>
                <Option value="bi-weekly">{t('Bi-Weekly')}</Option>
                <Option value="monthly">{t('Monthly')}</Option>
              </Select>
            </div>
            {recurrence !== 'none' && (
              <div className="form-row">
                <label>{t('Number of Recurrences')}:</label>
                <Input
                  type="number"
                  value={recurrenceCount}
                  onChange={(e) => setRecurrenceCount(e.target.value)}
                  min="1"
                  max="52"
                  placeholder={t('Enter number of occurrences')}
                />
              </div>
            )}
          </>
        )}

        <div className="form-row">
          <label>{t('Share with Users')}:</label>
          <Select
            mode="multiple"
            value={selectedUsers}
            onChange={(value) => setSelectedUsers(value)}
            style={{ width: '100%' }}
            placeholder={t('Select users to share with')}
            showSearch
            filterOption={(input, option) =>
              (option?.children || '')
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more')}`}
          >
            {users.map((user) => (
              <Option key={user.userid} value={user.userid}>
                {user.nickname} ({user.role})
              </Option>
            ))}
          </Select>
        </div>

        <div className="form-row">
          <label>{t('Share with Roles')}:</label>
          <Select
            mode="multiple"
            value={selectedRoles}
            onChange={(value) => setSelectedRoles(value)}
            style={{ width: '100%' }}
            placeholder={t('Select roles to share with')}
            maxTagCount={2}
          >
            <Option value="superadmin">{t('Superadmin')}</Option>
            <Option value="admin">{t('Admin')}</Option>
            <Option value="teacher">{t('Teacher')}</Option>
          </Select>
        </div>
      </Modal>

      <Modal
        title={t('Confirm Delete')}
        visible={isDeleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>
            {t('Cancel')}
          </Button>,
          <Button key="single" danger onClick={() => handleDeleteTask(false)}>
            {t('Delete Single Event')}
          </Button>,
          <Button key="group" danger onClick={() => handleDeleteTask(true)}>
            {t('Delete Entire Series')}
          </Button>,
        ]}
      >
        <p>{t('Do you want to delete this event or the entire series?')}</p>
      </Modal>
    </>
  );
};

export default TaskPopup;